import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon
} from '@mui/icons-material';
import Toast from '../../components/Toast';
import SEO from '../../components/SEO';
import authService from '../../services/authService';
import PasswordValidator from '../../components/PasswordValidator';

const ResetPassword = () => {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(null); // null = checking, true = valid, false = invalid
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  const navigate = useNavigate();
  const theme = useTheme();

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenVerified(false);
        setError('Invalid password reset link. Please request a new one.');
        return;
      }

      try {
        const response = await authService.verifyResetToken(token);
        if (response.success) {
          setTokenVerified(true);
        } else {
          setTokenVerified(false);
          setError('This password reset link is invalid or has expired.');
        }
      } catch (err) {
        console.error('Token verification error:', err);
        
        // Extract error message
        let errorMessage = 'This password reset link is invalid or has expired.';
        if (typeof err.message === 'string' && err.message.startsWith('{')) {
          try {
            const parsedError = JSON.parse(err.message);
            errorMessage = parsedError.message || errorMessage;
          } catch (e) {
            console.log('Failed to parse error JSON');
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setTokenVerified(false);
        setError(errorMessage);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!password) {
      setToast({
        open: true,
        message: 'Please enter a new password',
        severity: 'error'
      });
      return;
    }

    if (password.length < 8) {
      setToast({
        open: true,
        message: 'Password must be at least 8 characters long',
        severity: 'error'
      });
      return;
    }

    if (!/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/.test(password)) {
      setToast({
        open: true,
        message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
        severity: 'error'
      });
      return;
    }

    if (password !== confirmPassword) {
      setToast({
        open: true,
        message: 'Passwords do not match',
        severity: 'error'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await authService.resetPassword(token, password);
      
      if (response.success) {
        setSuccess(true);
        setToast({
          open: true,
          message: 'Password has been reset successfully!',
          severity: 'success'
        });
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Your password has been reset successfully. Please login with your new password.' 
            }
          });
        }, 3000);
      } else {
        throw new Error('Password reset failed. Please try again.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Extract error details
      let errorMessage = 'Password reset failed. Please try again.';
      
      if (typeof err.message === 'string' && err.message.startsWith('{')) {
        try {
          const parsedError = JSON.parse(err.message);
          errorMessage = parsedError.message || errorMessage;
        } catch (e) {
          console.log('Failed to parse error JSON');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setToast({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (tokenVerified === null) {
      // Loading state while verifying token
      return (
        <Box textAlign="center" my={4}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="body1">Verifying your reset link...</Typography>
        </Box>
      );
    } else if (tokenVerified === false) {
      // Invalid token
      return (
        <Box textAlign="center" my={4}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Invalid or expired link'}
          </Alert>
          <Typography variant="body1" paragraph>
            The password reset link is invalid or has expired.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please request a new password reset link.
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to="/forgot-password"
            sx={{ mt: 2, mr: 2 }}
          >
            Request New Link
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to="/login"
            sx={{ mt: 2 }}
          >
            Back to Login
          </Button>
        </Box>
      );
    } else if (success) {
      // Success state
      return (
        <Box textAlign="center" my={4}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Password reset successful!
          </Alert>
          <Typography variant="body1" paragraph>
            Your password has been reset successfully.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You will be redirected to the login page in a moment...
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to="/login"
            sx={{ mt: 2 }}
          >
            Login Now
          </Button>
        </Box>
      );
    } else {
      // Form state
      return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="New Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <PasswordValidator password={password} />

          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              background: 'linear-gradient(45deg, #1976d2, #2196f3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0, #1976d2)',
              },
              '&:disabled': {
                background: 'linear-gradient(45deg, #90caf9, #bbdefb)',
              },
              mb: 2
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Reset Password'
            )}
          </Button>
          
          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              <Link
                to="/login"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                Back to Login
              </Link>
            </Typography>
          </Box>
        </Box>
      );
    }
  };

  return (
    <>
      <SEO 
        title="Reset Password | Gravity Point Media" 
        description="Reset your Gravity Point Media account password" 
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 6 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent'
                }}
              >
                Reset Password
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create a new password for your account
              </Typography>
            </Box>

            {renderContent()}
          </Paper>
        </Container>
      </Box>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
};

export default ResetPassword;